import React from 'react';
import pt from 'prop-types';
import classNames from 'classnames';

import { isEmpty } from 'utils';

import Alert from 'components/alert';
import Icon from 'components/icon/icon';
import Modal from 'components/modal';
import Collection from 'components/collection';
import Pagination from 'components/collection/pagination';
import Scrollbars from 'components/scrollbars';

import './SelectRecords.scss';

class SelectRecordsModal extends React.PureComponent {
  static propTypes = {
    collection: pt.object,
    title: pt.string,
    recordsLabel: pt.string,
    action: pt.oneOfType([pt.string, pt.func]),
    width: pt.number,
    fields: pt.object,
    alert: pt.oneOfType([pt.string, pt.object]),
    // Optional node rendered directly above the list, e.g. a quick filter.
    toolbar: pt.node,
    loading: pt.bool,
    // The search the collection was loaded with, named by its empty state
    search: pt.string,
    disabledRecords: pt.object,
    // Optional (selectedRecords, selectableRecords) => { [id]: true | string }.
    // Rows disabled in reaction to the selection.
    getDisabledWithSelection: pt.func,
    // Optional (selectableRecords, selectedRecords) => { [id]: true }.
    // When selecting all, these records are left out of the selection.
    getSelectAllExcept: pt.func,
    // Optional; pages through the collection. The selection is cleared
    // whenever another page or search is loaded.
    onClickPage: pt.func,
    // (selectedIds, selectedRecords) => boolean; falsy keeps the modal open
    onSubmit: pt.func,
    onCancel: pt.func,
  };

  static contextTypes = {
    user: pt.object.isRequired,
    closeModal: pt.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      // Selected records by id, of the loaded page
      selected: {},
    };
  }

  componentDidUpdate(prevProps) {
    // A new collection is another page or search: its selection starts over
    if (
      prevProps.collection !== this.props.collection &&
      !isEmpty(this.state.selected)
    ) {
      this.setState({ selected: {} });
    }
  }

  getCollectionRecords() {
    const { collection, disabledRecords } = this.props;
    const records = collection?.results || [];

    // Disabled records can't be toggled by the user, so they should never
    // count towards "select all"/"select none" state or the final selection.
    return disabledRecords
      ? records.filter((record) => !disabledRecords[record.id])
      : records;
  }

  isPageSelected(records = this.getCollectionRecords()) {
    const { selected } = this.state;

    return records.length > 0 && records.every((record) => selected[record.id]);
  }

  getSelected() {
    return Object.keys(this.state.selected);
  }

  getSelectedRecords() {
    return Object.values(this.state.selected);
  }

  // The selection as the collection renders it: the header checkbox is
  // checked when the whole page is selected.
  getPageSelection(records) {
    const { selected } = this.state;

    if (this.isPageSelected(records)) {
      return { all: true, records: {}, except: {} };
    }

    return {
      all: false,
      records: Object.keys(selected).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {}),
      except: {},
    };
  }

  // Merges the static disabledRecords with any additional rows the caller
  // wants disabled in reaction to the current selection.
  getEffectiveDisabledRecords(selectedRecords, records) {
    const { disabledRecords, getDisabledWithSelection } = this.props;

    if (!getDisabledWithSelection) {
      return disabledRecords;
    }

    const dynamicDisabled = getDisabledWithSelection(selectedRecords, records);

    return isEmpty(dynamicDisabled)
      ? disabledRecords
      : { ...disabledRecords, ...dynamicDisabled };
  }

  onSubmit = async (event) => {
    event.preventDefault();

    const { onSubmit } = this.props;

    if (onSubmit) {
      this.setState({ loading: true });

      const result = await onSubmit(
        this.getSelected(),
        this.getSelectedRecords(),
      );

      this.setState({ loading: false });

      if (!result) {
        return;
      }
    }

    this.context.closeModal();
  };

  onClickCancel = (event) => {
    event.preventDefault();

    this.props.onCancel?.();
    this.context.closeModal();
  };

  // Toggles the page: clears the selection when anything is selected (a page
  // selected but for the select-all exceptions included), otherwise selects
  // every selectable record of it.
  onClickSelectAll = (event) => {
    event.preventDefault();

    if (!isEmpty(this.state.selected)) {
      this.setState({ selected: {} });
      return;
    }

    const { getSelectAllExcept } = this.props;
    const records = this.getCollectionRecords();
    const except =
      (getSelectAllExcept && getSelectAllExcept(records, [])) || {};
    const selected = {};

    records.forEach((record) => {
      if (!except[record.id]) {
        selected[record.id] = record;
      }
    });

    this.setState({ selected });
  };

  onClickSelectClear = (event) => {
    event.preventDefault();

    this.setState({ selected: {} });
  };

  onClickSelectRow = (event) => {
    event.preventDefault();

    const { id } = event.currentTarget.dataset;
    const record = this.getCollectionRecords().find(
      (record) => record.id === id,
    );
    const selected = { ...this.state.selected };

    if (selected[id]) {
      delete selected[id];
    } else if (record) {
      selected[id] = record;
    }

    this.setState({ selected });
  };

  render() {
    const {
      title = 'Select records',
      recordsLabel = 'records',
      action = 'Select',
      width = 760,
      collection,
      fields,
      alert,
      loading,
      toolbar,
      search,
      onClickPage,
    } = this.props;
    const collectionRecords = this.getCollectionRecords();
    const selectedRecords = this.getSelectedRecords();
    const isSelected = selectedRecords.length > 0;
    const actionLabel =
      typeof action === 'function' ? action(selectedRecords.length) : action;

    return (
      <Modal
        className="select-records-modal"
        title={title}
        actions={[
          {
            label: actionLabel,
            type: 'submit',
            onClick: this.onSubmit,
            disabled: !isSelected,
          },
          { label: 'Cancel', type: 'cancel', onClick: this.onClickCancel },
        ]}
        width={width}
        onClose={this.onClickCancel}
        cancel={false}
        closable={true}
        loading={loading}
      >
        {alert && (
          <Alert
            type="info"
            appearance="opaque"
            className="select-records-alert"
          >
            <Icon fa="info-circle" className="select-records-icon" />
            <div className="select-records-alert-message">
              {typeof alert === 'string' ? (
                alert
              ) : (
                <>
                  <span className="select-records-alert-message-title">
                    {alert.title}
                  </span>
                  <span className="select-records-alert-message-subtitle">
                    {alert.subtitle}
                  </span>
                </>
              )}
            </div>
          </Alert>
        )}
        {toolbar}
        <Scrollbars
          autoHeight
          autoHeightMin={collection?.totalPages > 1 ? '25rem' : 0}
          autoHeightMax={'25rem'}
          className={classNames('app-records-table', {
            'has-selection': isSelected,
          })}
        >
          <Collection
            title={recordsLabel}
            collection={collection}
            fields={fields}
            selection={this.getPageSelection(collectionRecords)}
            disabledRecords={this.getEffectiveDisabledRecords(
              selectedRecords,
              collectionRecords,
            )}
            searchable={false}
            selectable={true}
            locationQuery={search ? { search } : {}}
            onClickSelectRow={this.onClickSelectRow}
            onClickSelectAll={this.onClickSelectAll}
            onClickSelectClear={this.onClickSelectClear}
          />
        </Scrollbars>
        {/* Out of the scrolled table, so it stays in place on every page */}
        {onClickPage && collection && (
          <Pagination
            collection={collection}
            title={recordsLabel}
            onClickPage={onClickPage}
          />
        )}
      </Modal>
    );
  }
}

export default SelectRecordsModal;
